import React, { useState, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  RiskPreviewResult,
  useCreateRiskPipelineDefinitionMutation,
  useGetRiskListByIdQuery,
  useGetRiskPipelineDefinitionsQuery,
  usePreviewRiskMutation,
} from '../../../../redux/api/project-risk-api-slice'
import Risk from './risk'
import { toast } from 'react-toastify'
import { CreateRiskFormProps } from '../../../../shared/interfaces/project/risk/risk-inteface'

import { CustomRisk } from '../../../../shared/interfaces/project/risk/risk-list-interface'
import {
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentProject,
  selectSharedFromChatRiskQueryText,
  setSharedFromChatRiskQueryText,
} from '../../../../redux/application-slice'
import { skipToken } from '@reduxjs/toolkit/query'
import { POSTHOG } from '../../../../utils/posthog-constants'
import { usePostHog } from 'posthog-js/react'
import { Tooltip } from '@mantine/core'
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride'
import { SparklesIcon } from '@heroicons/react/24/outline'
import {
  useGetDocumentsListByProjectQuery,
  useGetUserProfileQuery,
  useRefineDefaultPositionMutation,
  useRefineRiskQuestionMutation,
} from '../../../../redux/api-slice'
import RiskPreviewPanel from './risk-preview-panel'

const riskTourSteps: Step[] = [
  {
    target: '.risk-form-container',
    content:
      "Welcome! Let's create your first custom risk. This form helps you define risks specific to your business needs.",
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '#riskName',
    content:
      'Start by giving your risk a clear, descriptive title. This helps you quickly identify it later.',
    placement: 'bottom',
  },
  {
    target: '#riskQueryText',
    content:
      'Enter the question you want answered about this risk. Make it specific and clear.',
    placement: 'bottom',
  },
  {
    target: '#riskDescription',
    content:
      'Add a description to provide context about why this risk matters to your organization. This field is optional.',
    placement: 'bottom',
  },
  {
    target: '#defaultPositionPromptTemplate',
    content:
      "Optionally define your organization's default stance on this risk. This helps with compliance checking.",
    placement: 'bottom',
  },
  {
    target: '.create-risk-button',
    content:
      "Once you've filled in the required fields, click here to create your custom risk!",
    placement: 'top',
    locale: {
      last: 'Close',
    },
  },
]

const joyrideStyles = {
  options: {
    primaryColor: '#2563eb',
    textColor: '#374151',
    backgroundColor: '#ffffff',
    arrowColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
    beaconSize: 36,
    zIndex: 10000,
  },
  tooltip: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    fontSize: '14px',
    padding: '20px',
    borderRadius: '8px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  tooltipContent: {
    padding: '0',
  },
  buttonNext: {
    backgroundColor: '#2563eb',
    borderRadius: '6px',
    color: '#ffffff',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    fontSize: '14px',
    fontWeight: '500',
    padding: '8px 16px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    outline: 'none',
  },
  buttonBack: {
    color: '#6b7280',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    fontSize: '14px',
    fontWeight: '500',
    marginRight: '12px',
    padding: '8px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  buttonSkip: {
    color: '#6b7280',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    fontSize: '14px',
    fontWeight: '500',
    padding: '8px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  buttonClose: {
    color: '#6b7280',
    width: '20px',
    height: '20px',
    padding: '0',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  floater: {
    arrow: {
      spread: 16,
    },
  },
}

interface ContextError {
  message: string
  suggestions: string[]
}

const CreateRiskForm: React.FC<CreateRiskFormProps> = ({ riskListName }) => {
  const currentProject = useSelector(selectCurrentProject)
  const { currentData: documents } = useGetDocumentsListByProjectQuery(
    currentProject ? { projectId: currentProject?.id } : skipToken,
    {
      pollingInterval: 0,
      refetchOnMountOrArgChange: true,
    }
  )
  const sharedFromChatRiskQueryText = useSelector(
    selectSharedFromChatRiskQueryText
  )
  const [riskName, setRiskName] = useState('')
  const [riskDescription, setRiskDescription] = useState('')
  const [defaultPositionPromptTemplate, setDefaultPositionPromptTemplate] =
    useState('')
  const [riskQueryText, setRiskQueryText] = useState(
    sharedFromChatRiskQueryText ?? ''
  )
  const [createRisk] = useCreateRiskPipelineDefinitionMutation()
  const [refineQuestion, { isLoading: isRefiningQuestion }] =
    useRefineRiskQuestionMutation()
  const [refineDefaultPosition, { isLoading: isRefiningDefaultPosition }] =
    useRefineDefaultPositionMutation()
  const { riskListId } = useParams<{ riskListId: string }>()
  const { data: riskPipelineDefinitions, isLoading: isLoadingRisks } =
    useGetRiskPipelineDefinitionsQuery(riskListId!)
  const dispatch = useDispatch()
  const posthog = usePostHog()
  const [runTour, setRunTour] = useState(false)
  const { data: currentUser } = useGetUserProfileQuery(undefined)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewData, setPreviewData] = useState<RiskPreviewResult | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewRisk, { isLoading: isPreviewing }] = usePreviewRiskMutation()
  const { projectId } = useParams()
  const [questionError, setQuestionError] = useState<ContextError | null>(null)
  const [positionError, setPositionError] = useState<ContextError | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await createRisk({
        risk_name: riskName,
        query_text: riskQueryText,
        risk_description: riskDescription,
        risk_list_template_id: riskListId!,
        default_position_prompt_template: defaultPositionPromptTemplate,
      }).unwrap()
      posthog?.capture(POSTHOG.custom_riskpipelinedefinition_created)
      setRiskName('')
      setRiskQueryText('')
      setRiskDescription('')
      setDefaultPositionPromptTemplate('')
      dispatch(setSharedFromChatRiskQueryText(''))
    } catch (error) {
      toast.error('Something went wrong. Please try again.')
    }
  }

  const handleRiskNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRiskName(e.target.value)
    },
    []
  )

  const handleRiskQueryTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setRiskQueryText(e.target.value)
    },
    []
  )

  const handleRiskDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRiskDescription(e.target.value)
    },
    []
  )

  const handleDefaultPositionPromptTemplateChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDefaultPositionPromptTemplate(e.target.value)
    },
    []
  )

  useEffect(() => {
    // Check if user data is loaded and if the feature flag is enabled
    if (!currentUser?.id || !currentUser?.feature_flags?.risk_form_tour) return

    const hasCompletedRiskTour = localStorage.getItem(
      `hasCompletedRiskTour_${currentUser?.id}`
    )

    if (!hasCompletedRiskTour) {
      // Small delay to ensure DOM is ready
      setTimeout(() => setRunTour(true), 500)
    }
  }, [currentUser?.id, currentUser?.feature_flags?.risk_form_tour])

  const { data: riskList, isLoading: riskListIsLoading } =
    useGetRiskListByIdQuery(riskListId ? parseInt(riskListId) : skipToken)

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status } = data
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

      if (finishedStatuses.includes(status)) {
        localStorage.setItem(`hasCompletedRiskTour_${currentUser?.id}`, 'true')
        setRunTour(false)
      }
    },
    [currentUser?.id]
  )

  const handleRefineQuestion = useCallback(async () => {
    if (!riskQueryText.trim()) {
      toast.warning('Please enter a question to refine')
      return
    }

    setQuestionError(null)

    try {
      const result = await refineQuestion({
        query_text: riskQueryText,
      }).unwrap()

      if (result.status === 'success') {
        setRiskQueryText(result.refined_text)
        toast.success('Question refined successfully!')
        posthog?.capture('ai_refine_question_used', {
          status: 'success',
        })
      } else {
        setQuestionError({
          message: result.error_message,
          suggestions: result.suggestions,
        })
        posthog?.capture('ai_refine_question_used', {
          status: 'insufficient_context',
        })
      }
    } catch (error: any) {
      // Handle 422 status
      if (
        error?.status === 422 &&
        error?.data?.status === 'insufficient_context'
      ) {
        setQuestionError({
          message: error.data.error_message,
          suggestions: error.data.suggestions,
        })
      } else {
        toast.error('Failed to refine question. Please try again.')
      }
      console.error('Error refining question:', error)
    }
  }, [riskQueryText, refineQuestion, posthog])

  const handleRefineDefaultPosition = useCallback(async () => {
    if (!defaultPositionPromptTemplate.trim()) {
      toast.warning('Please enter a default position to refine')
      return
    }

    setPositionError(null)

    try {
      const result = await refineDefaultPosition({
        default_position: defaultPositionPromptTemplate,
      }).unwrap()

      if (result.status === 'success') {
        setDefaultPositionPromptTemplate(result.refined_text)
        toast.success('Default position refined successfully!')
        posthog?.capture('ai_refine_default_position_used', {
          status: 'success',
        })
      } else {
        setPositionError({
          message: result.error_message,
          suggestions: result.suggestions,
        })
        posthog?.capture('ai_refine_default_position_used', {
          status: 'insufficient_context',
        })
      }
    } catch (error: any) {
      // Handle 422 status
      if (
        error?.status === 422 &&
        error?.data?.status === 'insufficient_context'
      ) {
        setPositionError({
          message: error.data.error_message,
          suggestions: error.data.suggestions,
        })
      } else {
        toast.error('Failed to refine default position. Please try again.')
      }
      console.error('Error refining default position:', error)
    }
  }, [defaultPositionPromptTemplate, refineDefaultPosition, posthog])

  const handlePreviewRisk = useCallback(async () => {
    // Validate required fields (remove description requirement for preview)
    if (!riskName.trim() || !riskQueryText.trim()) {
      toast.warning(
        'Please fill in the risk name and question before previewing'
      )
      return
    }
    setPreviewError(null)
    setIsPreviewOpen(true)

    try {
      const result = await previewRisk({
        project_uuid: projectId as string,
        query_text: riskQueryText,
        default_position_prompt_template:
          defaultPositionPromptTemplate || undefined,
        prompt_template: undefined,
        ranking_prompt_template: undefined,
        language: 'english',
      }).unwrap()

      setPreviewData(result)
      posthog?.capture('risk_preview_generated', {
        risk_level: result.risk_ranking,
        has_default_position: !!defaultPositionPromptTemplate,
      })
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        'Failed to generate risk preview. Please try again.'
      setPreviewError(errorMessage)
      console.error('Error previewing risk:', error)

      // Show more specific error messages
      if (errorMessage.includes('still processing')) {
        toast.warning(
          'Some documents are still being processed. Please wait a moment and try again.'
        )
      } else if (errorMessage.includes('not found')) {
        toast.error('Project not found. Please refresh the page and try again.')
      }
    }
  }, [
    riskName,
    riskQueryText,
    defaultPositionPromptTemplate,
    previewRisk,
    posthog,
  ])

  useEffect(() => {
    if (previewData) {
      setPreviewData(null)
    }
  }, [riskName, riskQueryText, riskDescription, defaultPositionPromptTemplate])

  useEffect(() => {
    if (questionError) {
      setQuestionError(null)
    }
  }, [riskQueryText])

  useEffect(() => {
    if (positionError) {
      setPositionError(null)
    }
  }, [defaultPositionPromptTemplate])

  return (
    <>
      {currentUser?.feature_flags?.risk_form_tour && (
        <Joyride
          callback={handleJoyrideCallback}
          continuous
          hideCloseButton
          run={runTour}
          scrollToFirstStep
          showProgress
          showSkipButton
          steps={riskTourSteps}
          styles={joyrideStyles}
        />
      )}
      <div className="h-full max-h-[calc(100vh-200px)] overflow-y-auto">
        <div className="risk-form-container mr-2 rounded-lg border border-[#ffffff00] bg-white p-2 px-4 shadow hover:border-yellow-400">
          <h2 className="mb-6 mt-2 text-lg font-semibold">
            <div className="mb-4 flex items-center">
              <PlusIcon className="mr-2 h-5 w-5 text-gray-900" /> Add a New Risk
              to
              <span className="ml-1.5 underline"> {riskListName ?? '...'}</span>
            </div>
          </h2>
          <form
            onSubmit={handleSubmit}
            className="mb-2 flex flex-col space-y-4"
          >
            <div className="flex-grow">
              <label
                htmlFor="riskName"
                className="block text-sm font-medium text-gray-700"
              >
                Title*
              </label>
              <input
                type="text"
                id="riskName"
                value={riskName}
                onChange={handleRiskNameChange}
                className="mt-2 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                placeholder="Warranty"
                required
              />
            </div>

            <div className="flex-grow">
              <label
                htmlFor="riskQueryText"
                className="block text-sm font-medium text-gray-700"
              >
                Question*
              </label>
              <div className="relative mt-2">
                <textarea
                  id="riskQueryText"
                  value={riskQueryText}
                  onChange={handleRiskQueryTextChange}
                  className={`w-full resize-y rounded-md pr-10 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 ${
                    questionError
                      ? 'border-amber-500 focus:border-amber-500 focus:ring-amber-200'
                      : 'border-gray-300'
                  }`}
                  placeholder="How long is the warranty period?"
                  rows={2}
                  required
                />
                <Tooltip
                  label="Use AI to improve and clarify this question"
                  position="top"
                  withArrow
                >
                  <button
                    type="button"
                    onClick={handleRefineQuestion}
                    disabled={
                      !riskQueryText.trim() ||
                      isRefiningQuestion ||
                      riskList?.user_permissions?.can_change !== true
                    }
                    className="absolute right-2 top-2 rounded p-1.5 text-gray-400 transition-all hover:bg-gray-100 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isRefiningQuestion ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                    ) : (
                      <SparklesIcon className="h-4 w-4" />
                    )}
                  </button>
                </Tooltip>
              </div>

              {questionError && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-amber-800">
                        {questionError.message}
                      </p>
                      <div className="mt-2 text-sm text-amber-700">
                        <p className="font-medium">
                          Try improving your question:
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                          {questionError.suggestions.map(
                            (suggestion, index) => (
                              <li key={index}>{suggestion}</li>
                            )
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-grow">
              <label
                htmlFor="riskDescription"
                className="block text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <input
                type="text"
                id="riskDescription"
                value={riskDescription}
                onChange={handleRiskDescriptionChange}
                className="mt-2 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                placeholder="Warranties tell us how long we need to guarantee products and workmanship are free from defects or issues."
              />
            </div>

            <div className="flex-grow">
              <label
                htmlFor="defaultPositionPromptTemplate"
                className="block text-sm font-medium text-gray-700"
              >
                Default Position Prompt
                <Tooltip
                  label="This is your organization's default stance on this risk. If you provide your default position, we will determine if the contract complies with this position."
                  position="top"
                  withArrow
                >
                  <QuestionMarkCircleIcon className="ml-1 inline h-4 w-4 cursor-help text-gray-400" />
                </Tooltip>
              </label>
              <div className="relative mt-2">
                <textarea
                  id="defaultPositionPromptTemplate"
                  value={defaultPositionPromptTemplate}
                  onChange={handleDefaultPositionPromptTemplateChange}
                  className={`w-full resize-y rounded-md pr-10 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 ${
                    positionError
                      ? 'border-amber-500 focus:border-amber-500 focus:ring-amber-200'
                      : 'border-gray-300'
                  }`}
                  placeholder="We do not provide warranties longer than one year."
                  rows={3}
                />
                <Tooltip
                  label="Use AI to enhance and clarify this default position"
                  position="top"
                  withArrow
                >
                  <button
                    type="button"
                    onClick={handleRefineDefaultPosition}
                    disabled={
                      !defaultPositionPromptTemplate.trim() ||
                      isRefiningDefaultPosition ||
                      riskList?.user_permissions?.can_change !== true
                    }
                    className="absolute right-2 top-2 rounded p-1.5 text-gray-400 transition-all hover:bg-gray-100 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isRefiningDefaultPosition ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                    ) : (
                      <SparklesIcon className="h-4 w-4" />
                    )}
                  </button>
                </Tooltip>
              </div>

              {positionError && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-amber-800">
                        {positionError.message}
                      </p>
                      <div className="mt-2 text-sm text-amber-700">
                        <p className="font-medium">
                          Try improving your position:
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-5">
                          {positionError.suggestions.map(
                            (suggestion, index) => (
                              <li key={index}>{suggestion}</li>
                            )
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handlePreviewRisk}
                disabled={
                  isPreviewing ||
                  !riskName.trim() ||
                  !riskQueryText.trim() ||
                  riskList?.user_permissions?.can_change !== true
                }
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isPreviewing ? 'Generating Preview...' : 'Preview Risk'}
              </button>
              <button
                disabled={riskList?.user_permissions?.can_change !== true}
                type="submit"
                className="create-risk-button rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                Create
              </button>
            </div>
          </form>

          <RiskPreviewPanel
            isOpen={isPreviewOpen}
            onToggle={() => setIsPreviewOpen(!isPreviewOpen)}
            isLoading={isPreviewing}
            previewData={previewData}
            error={previewError}
            documents={documents || []}
          />

          <hr className="mb-6 mt-4" />

          <h3 className="mb-3 text-xl font-semibold">
            <div className="mb-6 flex items-center">
              <ClipboardDocumentListIcon className="mr-2 h-5 w-5 text-gray-900" />{' '}
              Existing Risks
            </div>
          </h3>
          {isLoadingRisks ? (
            <p>Loading risks...</p>
          ) : riskPipelineDefinitions?.length ? (
            riskPipelineDefinitions.map((risk: CustomRisk) => (
              <Risk
                key={risk.id}
                id={risk.id}
                riskName={risk.risk_name}
                queryText={risk.query_text}
                description={risk.risk_description}
                permissions={riskList?.user_permissions}
                isLoadingRiskLists={riskListIsLoading}
                defaultPositionPromptTemplate={
                  risk.default_position_prompt_template
                }
              />
            ))
          ) : (
            <p>No risks found.</p>
          )}
        </div>
      </div>
    </>
  )
}

export default CreateRiskForm
